/**
 * BSCFlowDiagram.tsx - Horizontal BSC perspective cascade
 *
 * Displays the BSC flow: L&G -> Process -> Customer -> Financial
 * with active perspectives highlighted.
 * Per wobbly-nibbling-chipmunk.md Part 15.3
 */

import type { BSCPerspective } from '@models/oracle'
import './BSCFlowDiagram.css'

interface BSCFlowDiagramProps {
  bscFlow: string[]
  activePerspectives?: BSCPerspective[]
}

// All BSC perspectives in order
const ALL_PERSPECTIVES: BSCPerspective[] = [
  'Learning_Growth',
  'Internal_Process',
  'Customer',
  'Financial',
]

// Display labels and colors
const BSC_CONFIG: Record<
  BSCPerspective,
  { label: string; icon: string; color: string }
> = {
  Learning_Growth: { label: 'Learning & Growth', icon: '📚', color: '#3b82f6' },
  Internal_Process: { label: 'Internal Process', icon: '⚙️', color: '#10b981' },
  Customer: { label: 'Customer', icon: '👤', color: '#f97316' },
  Financial: { label: 'Financial', icon: '💰', color: '#ef4444' },
}

function BSCFlowDiagram({ bscFlow, activePerspectives }: BSCFlowDiagramProps) {
  // Determine which perspectives are active
  const activeSet = new Set(
    activePerspectives ||
      bscFlow.map((p) => {
        // Map flow strings back to perspective keys
        if (p === 'L&G' || p === 'Learning_Growth') return 'Learning_Growth'
        if (p === 'Process' || p === 'Internal_Process') return 'Internal_Process'
        if (p === 'Customer') return 'Customer'
        if (p === 'Financial') return 'Financial'
        return p
      })
  )

  return (
    <div className="bsc-flow-diagram">
      <h4>BSC Flow</h4>
      <div className="bsc-flow-container">
        {ALL_PERSPECTIVES.map((perspective, idx) => {
          const config = BSC_CONFIG[perspective]
          const isActive = activeSet.has(perspective)

          return (
            <div key={perspective} className="bsc-flow-item">
              <div
                className={`bsc-node ${isActive ? 'active' : 'inactive'}`}
                style={{
                  borderColor: isActive ? config.color : '#333',
                  background: isActive ? `${config.color}15` : '#1a1a1a',
                }}
              >
                <span className="bsc-icon">{config.icon}</span>
                <span
                  className="bsc-label"
                  style={{ color: isActive ? config.color : '#6b7280' }}
                >
                  {config.label}
                </span>
              </div>

              {idx < ALL_PERSPECTIVES.length - 1 && (
                <div
                  className={`bsc-arrow ${isActive && activeSet.has(ALL_PERSPECTIVES[idx + 1]) ? 'active' : ''}`}
                >
                  →
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Flow path display */}
      {bscFlow.length > 0 && (
        <div className="bsc-flow-path">
          <span className="path-label">Active Path:</span>
          <span className="path-value">{bscFlow.join(' → ')}</span>
        </div>
      )}
    </div>
  )
}

export default BSCFlowDiagram
